import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import type { Node } from '@xyflow/react';
import {
  DEVICE_NODE_MIN_HEIGHT,
  DEVICE_NODE_MIN_WIDTH,
} from '@ukrop-nm/shared';
import type { ObstacleRect } from './buildAvoidPath';

type EdgeRoutingValue = {
  obstacles: ObstacleRect[];
  /** While dragging nodes — skip heavy avoid search. */
  fast: boolean;
  peers: Array<{ id: string; source: string; target: string }>;
};

const EdgeRoutingContext = createContext<EdgeRoutingValue>({
  obstacles: [],
  fast: false,
  peers: [],
});

function isZoneNode(n: Node): boolean {
  if (n.className?.includes('ukrop-zone-node')) return true;
  return (n.data as { deviceType?: string } | undefined)?.deviceType === 'zone';
}

function toObstacle(n: Node): ObstacleRect | null {
  if (isZoneNode(n)) return null;
  const w = Math.max(
    Number(n.measured?.width) || 0,
    Number(n.width) || 0,
    typeof n.style?.width === 'number' ? n.style.width : 0,
    DEVICE_NODE_MIN_WIDTH,
  );
  const h = Math.max(
    Number(n.measured?.height) || 0,
    Number(n.height) || 0,
    typeof n.style?.height === 'number' ? n.style.height : 0,
    DEVICE_NODE_MIN_HEIGHT,
  );
  return { id: n.id, x: n.position.x, y: n.position.y, width: w, height: h };
}

export function EdgeRoutingProvider({
  nodes,
  edges,
  fast,
  children,
}: {
  nodes: Node[];
  edges: Array<{ id: string; source: string; target: string }>;
  fast: boolean;
  children: ReactNode;
}) {
  // Fingerprint positions so we don't rebuild obstacle objects on unrelated node field churn
  const posKey = useMemo(
    () =>
      nodes
        .map((n) => {
          const w =
            Number(n.measured?.width) ||
            Number(n.width) ||
            (typeof n.style?.width === 'number' ? n.style.width : 0) ||
            0;
          const h =
            Number(n.measured?.height) ||
            Number(n.height) ||
            (typeof n.style?.height === 'number' ? n.style.height : 0) ||
            0;
          return `${n.id}:${Math.round(n.position.x)}:${Math.round(n.position.y)}:${Math.round(w)}x${Math.round(h)}`;
        })
        .join('|'),
    [nodes],
  );

  const obstacles = useMemo(() => {
    if (fast) return [];
    const list: ObstacleRect[] = [];
    for (const n of nodes) {
      const o = toObstacle(n);
      if (o) list.push(o);
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- posKey captures geometry
  }, [fast, posKey]);

  const peers = useMemo(
    () => edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
    [edges],
  );

  const value = useMemo(
    () => ({ obstacles, fast, peers }),
    [obstacles, fast, peers],
  );

  return (
    <EdgeRoutingContext.Provider value={value}>{children}</EdgeRoutingContext.Provider>
  );
}

export function useEdgeRouting(): EdgeRoutingValue {
  return useContext(EdgeRoutingContext);
}
